import { Component, OnInit, AfterViewInit, Input, ViewChild, ElementRef, OnDestroy, OnChanges } from '@angular/core';
import { OrchestrationService } from '../../services/orchestration-service/orchestration.service';
import { select } from '@angular-redux/store';
import { SnackBarService } from "../../../common/services/snack-bar/snack-bar.service";
import { Observable, Subscription } from 'rxjs';
import { ApiResponseState } from '../../../store/api-response/api-response-state.model';
import { ApiResponseActions } from '../../../store/api-response/api-response-actions';
import { MatTableDataSource, MatSort, MatPaginator } from '@angular/material';
import { TranslationService } from '../../../common/services/languages/translation.service';
import { ApiResponseDispatcher } from '../../../store/api-response/api-response-dispatcher';
import { RemoteCommand } from './models/remote-command.model';
import { remote_mappings } from './models/remote-command-mapping';
import * as XLSX from 'xlsx';
import { transformDateTime } from '../../../common/helpers/utils';
import { NgIf } from '@angular/common';
import { MatFormField, MatLabel } from '@angular/material/select';
import { MatInput } from '@angular/material/input';
import { MatButton } from '@angular/material/button';
import { MatPaginator as MatPaginator_1 } from '@angular/material/paginator';
import { MatTable, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatCellDef, MatCell, MatHeaderRowDef, MatHeaderRow, MatRowDef, MatRow } from '@angular/material/table';
import { MatSort as MatSort_1, MatSortHeader } from '@angular/material/sort';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
    selector: 'app-remote-command',
    templateUrl: './remote-command.component.html',
    styleUrls: ['./remote-command.component.scss'],
    imports: [NgIf, MatFormField, MatLabel, MatInput, MatButton, MatPaginator_1, MatTable, MatSort_1, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatSortHeader, MatCellDef, MatCell, MatHeaderRowDef, MatHeaderRow, MatRowDef, MatRow, TranslatePipe]
})
export class RemoteCommandComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {
  /**
  * @Property to hold the displayed columns
  **/
  public showTable = true;
  /**
   * @Property to hold the displayed columns
   **/
  public displayedColumns: string[];
  /**
  * @Property to hold the data source
  **/
  public dataSource: MatTableDataSource<RemoteCommand>;
  /**
   * @arg paginator paginator
   */
  public paginator;
  /**
   * @arg content view page element
   */
  @ViewChild(MatPaginator, { static: false } as any) set matpaginator(paginator: ElementRef) {
    this.paginator = paginator;
    if (this.paginator && !!this.dataSource) {
      this.dataSource.paginator = this.paginator;
    }
  }
  /**
   * @arg sort sort
   */
  public sort;
  /**
  * @property input show remote command history
  */
  @Input() showRemoteHistory: boolean;
  /**
   * @arg content view sort element
   */
  @ViewChild(MatSort, { static: false } as any) set content(content: ElementRef) {
    this.sort = content;
    if (this.sort && !!this.dataSource) {
      this.dataSource.sort = this.sort;
    }
  }
  /**
   * @property remoteCommandSubscriber used to store the remote command subscriber
   */
  private remoteCommandSubscriber: Subscription;
  /**
  * @Property to observe the redux customer store
  **/
  @select('api') readonly apiObservable: Observable<ApiResponseState>;
  /**
  * @property Will contain vehicle details payload
  */
  @Input() vehicleDetails: object;
  /**
   * @method applyFilter method will take the filterValue and filter over the dataSourse.
   * @param string filterValue anyvalue
   */
  public applyFilter(filterValue: string) {
    this.dataSource.filter = filterValue.trim().toLowerCase();
    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }
  constructor(
    private snackBarService: SnackBarService,
    public orch: OrchestrationService,
    public translate: TranslationService,
    public apiDispatcher: ApiResponseDispatcher
  ) {
    this.remoteCommandSubscriber = this.apiObservable.subscribe((api) => {
      const temp = api.apiAction === ApiResponseActions.SET_REMOTE_COMMAND && api.remoteCommand['type'] === 'success'
        && !!api.remoteCommand['payload'] && api.remoteCommand['vin'] === this.vehicleDetails['vin'] ?
        this.remoteCommandSuccess(api.remoteCommand) : api.remoteCommand['type'] === 'error' ? this.remoteCommandError(api.remoteCommand) : null;
    });
  }
  ngOnInit() {
    // Initialize data source early to set up sorting configuration
    this.dataSource = new MatTableDataSource([]);
    
    // Set up the sortingDataAccessor before any data is loaded
    this.dataSource.sortingDataAccessor = (item, property) => {
      if (property === 'commandDateTime') {
        return item.commandDateTimeRaw;
      }
      return item[property];
    };
    
    this.getRemoteCommand();
  }
  
  ngAfterViewInit() {
    this.initializeSort();
  }

  /**
   * Initializes or re-initializes the sort connection and applies default sort.
   */
  private initializeSort(): void {
    setTimeout(() => {
      if (this.sort && this.dataSource) {
        this.dataSource.sort = this.sort;
        this.sort.disableClear = true;
        
        // Apply default sort to 'commandDateTime' column descending
        this.sort.active = 'commandDateTime';
        this.sort.direction = 'desc';
        this.sort.sortChange.emit({ active: 'commandDateTime', direction: 'desc' });
      }
    });
  }
  
  ngOnDestroy() {
    this.remoteCommandSubscriber.unsubscribe();
  }
  ngOnChanges() {
    if (this.showRemoteHistory === true) {
      this.getRemoteCommand();
    }
  }
  /**
   * @function remoteCommandSuccess  api call success
   */
  public remoteCommandSuccess(response: object): void {
    this.setTableData(response['payload']);
  }
  /**
   * @function setTableData set table data
   */
  setTableData(commandsHistory: Array<any>) {
    this.showTable = commandsHistory.length > 0 ? true : false;
    
    const tableSource = this.getTableSource(commandsHistory);
    
    // Simply assign the data without pre-sorting - let MatSort handle all sorting
    this.dataSource.data = tableSource;
    this.displayedColumns = ['deviceType', 'command', 'commandDateTime', 'status', 'dcmErrorCode', 'resultDesc'];
    
    if (this.dataSource && this.paginator) {
      this.dataSource.paginator = this.paginator;
    }
    
    // Re-initialize sort after data is loaded
    this.initializeSort();
  }



  /**
   * @function isValidDate will return a boolean value based on input date.
   */
  isValidDate(d) {
    if (!d) {
      return false;
    }

    if (isNaN(new Date(d).getTime())) {
      return false;
    }

    return true;
  }

  /**
   * @function getTableSource get table data
   */
  getTableSource(commandsHistory: Array<any>): Array<any> {
    const tableData = [];
    commandsHistory.forEach(command => {
      const formattedCommandDate = transformDateTime(command.commandDateTime);
      const row = {
        deviceType: !!command.deviceType ? command.deviceType.toLowerCase() === 'ios' ? 'iOS' : command.deviceType.toLowerCase() === 'watchos' ? 'WatchOS' : `${command.deviceType[0].toUpperCase()}${command.deviceType.slice(1).toLowerCase()}` : '',
        command: !!command.command ? remote_mappings[command.command.toLowerCase()] : '',
        commandDateTime: !!command.commandDateTime ? formattedCommandDate : '',
        commandDateTimeRaw: !!command.commandDateTime ? new Date(command.commandDateTime) : null,
        status: !!command.displayStatus ? command.displayStatus : '',
        dcmErrorCode: !!!!command.phases && !!!!command.phases[0] && !!command.phases[0].dcmErrorCode ? command.phases[0].dcmErrorCode : '',
        resultDesc: !!!!command.phases && !!!!command.phases[0] && !!command.phases[0].resultDesc ? command.phases[0].resultDesc : '',
      };
      tableData.push(row);
    });
    return tableData;
  }
  /**
   * @function remoteCommandError  api call error
   */
  public remoteCommandError(error: object): void {
    this.showTable = false;
    this.apiDispatcher.setRemoteCommandResponse({});
    this.snackBarService.showToast('Remote command history failed to load.');
  }
  /**
   * @function exportTOExcel exports the table data to excel file
   */
  public exportTOExcel() {
    const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(this.dataSource.data);
    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
    XLSX.writeFile(wb, 'remote-commands.xlsx');
  }
  /**
   * @function getRemoteCommand make a call to getRemoteCommand
   */
  getRemoteCommand() {
    const generation = this.vehicleDetails['generation'];
    let paramsObj = { vin: this.vehicleDetails['vin'] };
    if (generation === 'GR86') {
      paramsObj['x-guid'] = this.vehicleDetails['remoteUserGuid'];
      paramsObj['gen'] = 'GR86';
    }
    if (generation === '24MM') {
      paramsObj['generation'] = generation;
    }
    const type = generation === '24MM' ? 'remote-command-24mm' : 'remote-command';
    this.orch.getRemoteCommand({ type, params: paramsObj }, generation);
  }
}
